import React = require("react");
import { SingleColumnLayout } from '../../../designSystem/layouts/SingleColumnLayout';
import { LoadingPageOverlay } from "../../../designSystem/LoadingPageOverlay";
import * as dateMath from 'date-arithmetic';
import { txt } from "../../../txt";
import { createActivity, getSafetyInspectionActivities } from "../../../api/activityClientApi";
import { Flex } from "../../../designSystem/compositeComponents/FlexHelpers";
import { AmorgMenu, AmorgMenuContent } from "../../../designSystem/amorgMenu";
import { Redirect } from "react-router-dom";
import { isSameDate } from "../../../designSystem/formField/DateStartEndTimeFields";
import { P } from "../../../designSystem/typography/Body";
import { toTextDateString, toTimeString } from "../../../utils/date";
import { SafetyInspectionActivityListing } from "../../../amorg-common/DTOs/SafetyInspectionActivityListing";
import { GenericActivityListingView } from "../common/GenericActivityListingView";
import { ToolBarButton } from "../../../designSystem/compositeComponents/ButtonToolBar";
import { H2 } from "../../../designSystem/typography/Heading";
import { rng } from "../../../amorg-common";
import { CardButton } from "../../../designSystem/CardButton";


type State = {
	loading:boolean,
	activities:SafetyInspectionActivityListing[],
    redirectId:string
}

export function SafetyInspectionListingView(): JSX.Element
{
	const [state, setState] = React.useState<State>({
        loading:true,
        activities:[],
        redirectId:''
    });

    const [redirectToInspectionPoints, setRedirectToInspectionPoints] = React.useState(false);

    React.useEffect(()=>{
        const getData = async () => {
            const activities = await getSafetyInspectionActivities();
            activities.sort((a,b)=> b.start.getTime() - a.start.getTime());
            // Get activities convert start and end from strings (JSON transport) to dates
            setState({activities, loading:false, redirectId:''});
        }
        getData();
    },[])

    async function handleClickNew()
    {
        const now = new Date();

        const activityId = rng.getRandomId();
        const startDate = dateMath.add(new Date(now.getFullYear(), now.getMonth(), now.getDate(), now.getHours(), 0, 0, 0), 1, 'day');
        const endDate = dateMath.add(startDate, 1, 'day');

        setState({...state, loading:true});

        const result = await createActivity(
            activityId,
            'SafetyInspection',
            txt.NewActivityType_SafetyInspection,
            '',
            startDate,
            endDate,
            ''
        );

        if(result.isSuccess){
            setState({...state, redirectId:activityId});
        }
    }

    return (
        <SingleColumnLayout
            noDivider
            contentContainer={
                <>
                    {state.redirectId !== '' &&
                        <Redirect to={`/safetyinspections/${state.redirectId}`} />
                    }

                    {redirectToInspectionPoints&&
                        <Redirect to={`/inspectionpoints`} />
                    }

                    <LoadingPageOverlay loading={state.loading} />
                    
                    <GenericActivityListingView
                        headerIcon="fact_check"
                        headerText="Aktuelle sikkerhedsrunderinger"
                        pastHeaderText="TIDLIGERE SIKKERHEDSRUNDERINGER"
                        newActivityButtonText="Opret ny sikkerhedsrundering"
                        activities={state.activities}
                        onClick={(id) => setState({...state, redirectId:id})}
                        onClickNew={handleClickNew}
                        toolBarButtons={
                            <ToolBarButton variant="text" icon='navigation_settings' tooltip="Indstilling af kontrolpunkter"
                                onClick={() => setRedirectToInspectionPoints(true)} />
                        }
                    />
                </>
            }
        />
	);
}

type SafetyInspectionViewProps = {
    listing:SafetyInspectionActivityListing
}

export function SafetyInspectionView(props:SafetyInspectionViewProps):JSX.Element
{
    const content:AmorgMenuContent[] = [];
    if(props.listing.locations.length === 0)
    {
        content.push(
        {
            type:'leadingContent',
            styleVariant: 'destructive',
            leadingContent:{ type:'icon', iconName:'actions_unavailable' },
            primaryContent:{ type:'text', text:'ingen lokationer tilføjet'}
        });
    }
    for(const l of props.listing.locations)
    {
        const pending = l.facilities.reduce((acc, f) => acc + f.inspections.pending, 0);
        const passed = l.facilities.reduce((acc, f) => acc + f.inspections.passed, 0);
        const failed = l.facilities.reduce((acc, f) => acc + f.inspections.failed, 0);

        if(pending + passed + failed === 0)
        {
            content.push(
                {
                    type:'leadingContent',
                    styleVariant: 'destructive',
                    leadingContent:{ type:'icon', iconName:'facility_domain' },
                    primaryContent:{ type:'text', text:`${l.name} - ingen kontrolpunkter tilføjet`}
                });
        }
        else if(pending > 0)
        {
            content.push(
                {
                    type:'leadingContent',
                    styleVariant: 'default',
                    leadingContent:{ type:'icon', iconName:'facility_domain' },
                    primaryContent:{ type:'text', text:`${l.name} - ${pending} ${pending===1 ? 'kontrolpunkt' : 'kontrolpunkter'} mangler at blive gennemført`}
                });
        }
        else if(failed > 0)
        {
            content.push(
                {
                    type:'leadingContent',
                    styleVariant: 'destructive',
                    leadingContent:{ type:'icon', iconName:'facility_domain' },
                    primaryContent:{ type:'text', text:`${l.name} - ${failed} ${failed===1 ? 'kontrolpunkt' : 'kontrolpunkter'}  ikke-godkendt`}
                });
        }
        else if(passed > 0 && failed === 0 && pending === 0)
        {
            content.push(
                {
                    type:'leadingContent',
                    styleVariant: 'default',
                    leadingContent:{ type:'icon', iconName:'facility_domain' },
                    primaryContent:{ type:'text', text:`${l.name} - Alle kontrolpunkter godkendt!`}

                });
        }
    }


    function handleOnClick(){
        document.location=`/safetyinspections/${props.listing.id}`;
    }


    return (
        
        <CardButton onClick={ handleOnClick }>
            {/* <div>
                <PrettyPrintJson data={props.listing} />
            </div> */}
            <Flex flexDirection="column" gap={24}>
                <Flex flexDirection="column">
                    <H2>{props.listing.title}</H2>
                    <P>
                    {!isSameDate(props.listing.start, props.listing.end) ? 
                        toTextDateString(props.listing.start) + " kl. " + toTimeString(props.listing.start) + " - " + toTextDateString(props.listing.end) + " kl. " + toTimeString(props.listing.end) 
                        : 
                        toTextDateString(props.listing.start) + " kl. " + toTimeString(props.listing.start) + " - " + toTimeString(props.listing.end)
                    }
                    </P>
                </Flex>
                <AmorgMenu content={content} />
            </Flex>
        </CardButton>
    );
}